import { Fragment, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Gem, Map, Megaphone, UserCircle2, LifeBuoy } from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { NAV_GROUPS, type NavItem, type NavGroup } from "@/lib/navigation";
type LucideIcon = ComponentType<SVGProps<SVGSVGElement>>;
import { cn } from "@/lib/cn";
import { Logo } from "@/components/ui/Logo";
import { useAuth, expandRoles } from "@/lib/auth";
import { useMyDossier, useParcoursCapabilities, useUnreadMessagesCount } from "@/lib/data";
import type { UserRole } from "@/lib/supabase";
import { ChangelogDialog } from "@/components/admin/ChangelogDialog";
import { hasUnseenChangelog } from "@/lib/changelog";

const FOOTER_ITEMS: readonly NavItem[] = [
  { to: "/app/profile", labelKey: "app.nav.profile", icon: UserCircle2 },
  { to: "/app/help", labelKey: "app.nav.help", icon: LifeBuoy },
];

const ROLE_ORDER: readonly UserRole[] = [
  "salarie",
  "drh",
  "consultant_aon",
  "chef_projet_aon",
  "admin_aon",
];

interface AppSidebarProps {
  className?: string;
  onNavigate?: () => void;
}

export function AppSidebar({ className, onNavigate }: AppSidebarProps) {
  const { t } = useTranslation();
  const { roles } = useAuth();
  const { data: unreadCount = 0 } = useUnreadMessagesCount();
  const isAdmin = roles.includes("admin_aon");

  // État du dialog Changelog (visible uniquement pour les admins)
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [hasUnseen, setHasUnseen] = useState(false);

  // Recalcule l'état du badge à chaque ouverture/fermeture du dialog.
  useEffect(() => {
    if (isAdmin) setHasUnseen(hasUnseenChangelog());
  }, [isAdmin, changelogOpen]);

  // Expansion hiérarchique des rôles : un admin_aon voit toutes les sections
  // Aon (production + admin), un chef_projet_aon voit aussi le périmètre
  // consultant. Évite d'avoir à cumuler manuellement les rôles dans la DB.
  const effectiveRoles = expandRoles(roles);

  const { data: myDossier } = useMyDossier();
  const { parcoursType } = useParcoursCapabilities({
    operationId: myDossier ? myDossier.operation_id : undefined,
  });
  const hideRdv = parcoursType === "digital";

  // Fusionne les groupes par labelKey (consultant_aon et chef_projet_aon
  // partagent `app.nav.group_aon`) — les items sont fusionnés en
  // déduplicant par `to`.
  const visibleGroups: NavGroup[] = [];
  for (const role of ROLE_ORDER) {
    if (!effectiveRoles.includes(role)) continue;
    const group = NAV_GROUPS[role];
    if (!group) continue;

    const existing = visibleGroups.find((g) => g.labelKey === group.labelKey);
    if (existing) {
      const seenPaths = new Set(existing.items.map((it) => it.to));
      const merged: NavItem[] = [...existing.items];
      for (const item of group.items) {
        if (seenPaths.has(item.to)) continue;
        merged.push(item);
        seenPaths.add(item.to);
      }
      // Remplace dans le tableau (mutation contrôlée du tableau, pas du groupe d'origine)
      const idx = visibleGroups.indexOf(existing);
      visibleGroups[idx] = { ...existing, items: merged };
      continue;
    }

    visibleGroups.push(group);
  }

  // Parcours 100% digital : la prise de rendez-vous n'existe pas, on retire
  // l'entrée de menu. On ne masque QUE sur un signal POSITIF (type de parcours
  // effectivement lu) pour ne pas faire disparaître l'entrée au chargement.
  const filteredGroups: NavGroup[] = visibleGroups.map((g) =>
    hideRdv ? { ...g, items: g.items.filter((it) => it.to !== "/app/rdv") } : g,
  );

  // Injecte un badge dynamique "non-lus" sur les liens messagerie.
  // On clone la structure pour ne pas muter la constante NAV_GROUPS.
  const badgedGroups: NavGroup[] = filteredGroups.map((g) => ({
    ...g,
    items: g.items.map((it) =>
      (it.to === "/app/messages" ||
        it.to === "/app/aon/messages" ||
        it.to === "/app/drh/messages") &&
      unreadCount > 0
        ? { ...it, badge: unreadCount > 99 ? "99+" : String(unreadCount) }
        : it,
    ),
  }));

  return (
    <aside
      aria-label="Menu de l’espace connecté"
      className={cn("flex h-full w-64 flex-col border-r border-line bg-bg", className)}
    >
      {/* Branding header */}
      <div className="flex items-center border-b border-line px-5 py-5">
        <Logo />
      </div>

      {/* Scrollable navigation */}
      <nav aria-label="Navigation principale" className="flex-1 overflow-y-auto px-3 py-5">
        {visibleGroups.length === 0 ? (
          <div className="px-3 py-4 text-xs text-ink-muted">
            Aucun rôle attribué. Contactez votre administrateur pour activer votre accès.
          </div>
        ) : (
          badgedGroups.map((group, index) => (
            <Fragment key={group.key}>
              {index > 0 && <div className="my-4 h-px bg-line/60" />}
              <div className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-ink-subtle">
                {t(group.labelKey)}
              </div>
              <ul className="flex flex-col gap-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <SidebarLink {...item} onClick={onNavigate} />
                  </li>
                ))}
              </ul>

              {/* Bloc « Produit » : Roadmap + Nouveautés regroupés en bas de
                  l'Administration, dans un encart légèrement teinté pour les
                  distinguer des entrées de gestion courante. */}
              {isAdmin && group.key === "admin_aon" && (
                <div className="mt-3 rounded-xl border border-brand/15 bg-brand-muted/20 p-1.5">
                  <ul className="flex flex-col gap-0.5">
                    <li>
                      <SidebarLink
                        to="/app/admin/roadmap"
                        labelKey="app.nav.roadmap"
                        icon={Map}
                        tinted
                        onClick={onNavigate}
                      />
                    </li>
                    <li>
                      <SidebarActionButton
                        label="Nouveautés"
                        icon={Megaphone}
                        hasBadge={hasUnseen}
                        tinted
                        onClick={() => {
                          setChangelogOpen(true);
                          onNavigate?.();
                        }}
                      />
                    </li>
                  </ul>
                </div>
              )}
            </Fragment>
          ))
        )}
      </nav>

      {/* Dialog Changelog — monté ici pour rester accessible même quand la
          sidebar mobile est fermée (le state est dans le composant). */}
      {isAdmin && <ChangelogDialog open={changelogOpen} onClose={() => setChangelogOpen(false)} />}

      {/* Footer items */}
      <div className="shrink-0 border-t border-line p-3">
        <ul className="flex flex-col gap-0.5">
          {FOOTER_ITEMS.map((item) => (
            <li key={item.to}>
              <SidebarLink {...item} onClick={onNavigate} />
            </li>
          ))}
        </ul>
      </div>

      {/* Brand footer */}
      <div className="shrink-0 border-t border-line bg-bg-subtle px-5 py-4">
        <div className="flex items-center gap-2 text-[11px] text-ink-subtle">
          <Gem className="size-3.5 text-brand" />
          <span>Aon Wealth — BRI Platform</span>
        </div>
      </div>
    </aside>
  );
}

interface SidebarLinkProps extends NavItem {
  onClick?: () => void;
  /** Variante teintée (encart « Produit ») : texte légèrement coloré marque. */
  tinted?: boolean;
}

function SidebarLink({ to, end, labelKey, icon: Icon, badge, onClick, tinted }: SidebarLinkProps) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          isActive
            ? "bg-brand-muted/70 text-brand"
            : tinted
              ? "text-brand hover:bg-brand-muted/60"
              : "text-ink-muted hover:bg-bg-subtle hover:text-ink",
        )
      }
    >
      <Icon className="size-4 shrink-0" />
      <span className="flex-1 truncate">{t(labelKey)}</span>
      {badge && (
        <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1.5 text-[10px] font-semibold text-brand-foreground">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

// -----------------------------------------------------------------------------
// SidebarActionButton : variante de SidebarLink pour les items qui ne sont pas
// des routes (ouvrent un dialog ou déclenchent une action). Reprend exactement
// le même style visuel que SidebarLink pour la cohérence.
// -----------------------------------------------------------------------------
function SidebarActionButton({
  label,
  icon: Icon,
  hasBadge,
  onClick,
  tinted,
}: {
  label: string;
  icon: LucideIcon;
  hasBadge?: boolean;
  onClick: () => void;
  /** Variante teintée (encart « Produit ») : texte légèrement coloré marque. */
  tinted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "focus-ring group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors",
        tinted
          ? "text-brand hover:bg-brand-muted/60"
          : "text-ink-muted hover:bg-bg-subtle hover:text-ink",
      )}
    >
      <Icon className="size-4 shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {hasBadge && (
        <span
          aria-hidden
          className="ml-auto inline-flex size-2 shrink-0 rounded-full bg-danger"
          title="Nouvelles entrées non vues"
        />
      )}
    </button>
  );
}
